import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import AuthorAddForm from "./AuthorAddForm";
import AuthorEditForm from "./AuthorEditForm";

import { createUploadObject, splitMultilineList } from "../../../../helper-functions/shared/miscUtils";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/authors";
import { applyAuthorAliases, applyAuthorExclusions, applyAuthorsAliases, applyAuthorsExclusions, createAuthor, deleteAuthors, queryAuthors, updateAuthor } from "../../../../helper-functions/api/authorQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing authors.
 * If no data found, show error message instead.
 */
export default function AuthorsTable(props) {
    const authors = props.backend.authors;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new author",
            message: "Click 'Save' to confirm changes.",
            component: <AuthorAddForm {...props} callback={(confirm, options) => handleCreate(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete authors(s)",
            message: `Are you sure you want to delete the ${authors.selected.length} selected author(s)?

            This action cannot be undone.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit author",
            message: "Click 'Update' to confirm changes.",
            component: <AuthorEditForm {...props} row={authors.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const handleCreate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);
        body.aliases = splitMultilineList(row.aliases);
        body.exclusions = splitMultilineList(row.exclusions);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING });
        createAuthor(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = authors.selected.map((q) => q.id);
        deleteAuthors(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);
        body.aliases = splitMultilineList(row.aliases);
        body.exclusions = splitMultilineList(row.exclusions);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING });
        updateAuthor(props, { body, id: row.id });
    };

    const showApplyExclusionsDialog = () => {
        const handle = authors.selected.length > 1 ? handleMultiApplyExclusions : handleApplyExclusions;

        props.showDialog({
            title: "Apply author exclusions",
            message: `Scan resources linked to the ${authors.selected.length} selected author(s) and unlink any that only matched excluded phrases?

            Other authors on those resources are left unchanged. Manually assigned authors whose names are not in the title or description are also left unchanged.`,
            actionTitle: "Apply exclusions",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const handleApplyExclusions = (confirm) => {
        if (!confirm) return;

        const ids = authors.selected.map((author) => author.id);
        applyAuthorExclusions(props, { id: ids?.[0] });
    };

    const handleMultiApplyExclusions = (confirm) => {
        if (!confirm) return;

        const ids = authors.selected.map((author) => author.id);
        applyAuthorsExclusions(props, { body: { ids } });
    };

    const showApplyAliasesDialog = () => {
        const handle = authors.selected.length > 1 ? handleMultiApplyAliases : handleApplyAliases;

        props.showDialog({
            title: "Apply author aliases",
            message: `Scan existing resources and link the ${authors.selected.length} selected author(s) wherever their name or an alias appears?

            Exclusion phrases are still applied first, so sponsor-only mentions do not count. Other authors on those resources are left unchanged.`,
            actionTitle: "Apply aliases",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const handleApplyAliases = (confirm) => {
        if (!confirm) return;

        const ids = authors.selected.map((author) => author.id);
        applyAuthorAliases(props, { id: ids?.[0] });
    };

    const handleMultiApplyAliases = (confirm) => {
        if (!confirm) return;

        const ids = authors.selected.map((author) => author.id);
        applyAuthorsAliases(props, { body: { ids } });
    };

    return (
        <PaperFragment
            className="tour-authors-2 tour-authors-3"
            title={
                <StackedHeader
                    {...props}
                    title="Authors"
                    subtitle="View and manage authors."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected authors",
                            disabled: authors.selected.length < 1,
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected author",
                            onClick: showUpdateDialog,
                            disabled: authors.selected.length !== 1
                        },
                        {
                            icon: <FilterAltOffIcon />,
                            tooltip: "Apply exclusions to linked resources",
                            onClick: showApplyExclusionsDialog,
                            disabled: authors.selected.length < 1
                        },
                        {
                            icon: <PersonAddIcon />,
                            tooltip: "Apply aliases to matching resources",
                            onClick: showApplyAliasesDialog,
                            disabled: authors.selected.length < 1
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New author",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={authors}
                    columns={columns(props)}
                    query={(options) => queryAuthors(props, options)}
                    empty={getLanguage().NO_AUTHORS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
